import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import { voiceAudioSrc } from "../../config/voiceAudio";
import { checkRole } from "../recipe_page/recipe_page";
import BasicButton from "../basics/BasicButton";
import NotificationReachCard from "./NotificationReachCard";
import { useConfirm } from "../../ui/components/ConfirmDialog";
import {
  type NotifDef,
  type NotifSample,
  type ChatNotificationStyle,
  useGetNotifDefsQuery,
  useGetNotifMutesQuery,
  useLazyGetNotifDefQuery,
  useUpdateNotifDefMutation,
  useMuteNotifMutation,
  useUnmuteNotifMutation,
  useSetServicePolicyMutation,
  useGetChatNotificationStyleQuery,
  useSetChatNotificationStyleMutation,
  useSendChatStyleTestMutation,
  useGetVoiceConfigQuery,
  useSetVoiceConfigMutation,
  useTestVoiceConfigMutation,
  useGetVoiceLogsQuery,
  type VoiceSurface,
  type VoiceLogEvent,
} from "../../services/rtkApi/notificationsAdminApi";
import {
  type NotificationLogEntry,
  useLazyGetNotificationLogsQuery,
} from "../../services/rtkApi/schedulerAdminApi";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffe9d6;
  align-items: stretch;
  padding: 20px;
  min-height: 100vh;
  max-width: 1100px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

const Scroll = styled.div`
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }
  th {
    font-weight: 600;
    color: #333;
    position: sticky;
    top: 0;
    background: #fafafa;
    z-index: 1;
  }
`;

const LogScroll = styled.div`
  max-height: 480px;
  overflow: auto;
`;

const Hint = styled.p`
  font-size: 0.85rem;
  color: #555;
  margin: 0 0 12px;
  line-height: 1.4;
`;

const emojiCell: React.CSSProperties = { width: 64 };
const numCell: React.CSSProperties = { textAlign: "right", fontVariantNumeric: "tabular-nums" };

function statusStyle(status: string): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: "0.75rem",
    fontWeight: 600,
  };
  if (status === "sent") return { ...base, background: "#d4edda", color: "#155724" };
  if (status === "skipped") return { ...base, background: "#fff3cd", color: "#856404" };
  if (status === "failed") return { ...base, background: "#f8d7da", color: "#721c24" };
  return { ...base, background: "#e9ecef", color: "#333" };
}

function fmtTs(ts?: string): string {
  if (!ts) return "—";
  return String(ts).replace("T", " ").slice(0, 19);
}

// per-service delivery policy primary options (fanout clears the policy)
type PrimaryChoice = "fanout" | "tg" | "push";
type FallbackChoice = "none" | "tg" | "push";

// --- chat notification style picker ---

const STYLE_LABELS: Record<ChatNotificationStyle, { label: string; desc: string }> = {
  sender_title: {
    label: "Sender as the title",
    desc: "Title shows “sitebot · general”; the body is just the message. Cleanest native chat look.",
  },
  single_line: {
    label: "Single line in the body",
    desc: "Title stays “n450s”; the whole thing is one line, e.g. “(general) sitebot: …”.",
  },
};

// mirror the backend formatter so the preview matches what actually gets pushed.
function chatPreview(
  style: ChatNotificationStyle,
  channel: "general" | "personal",
): { title: string; body: string } {
  const sender = "sitebot";
  const sample =
    channel === "general"
      ? "the new card just dropped — go claim it"
      : "finished that thing you asked about — want the details?";
  const channelLabel = channel === "general" ? "general" : "";
  if (style === "sender_title") {
    return { title: channelLabel ? `${sender} · ${channelLabel}` : sender, body: sample };
  }
  const line = channelLabel ? `(${channelLabel}) ${sender}: ${sample}` : `${sender}: ${sample}`;
  return { title: "n450s", body: `💬 ${line}` };
}

const previewBox: React.CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  background: "#f3f4f6",
  border: "1px solid #e2e5ea",
  borderRadius: 10,
  padding: "10px 12px",
  maxWidth: 360,
};
const previewIcon: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  flex: "0 0 auto",
  objectFit: "cover",
};
const previewTitle: React.CSSProperties = { fontWeight: 700, fontSize: "0.85rem", color: "#111" };
const previewBody: React.CSSProperties = {
  fontSize: "0.82rem",
  color: "#333",
  lineHeight: 1.35,
  wordBreak: "break-word",
};

function rtkErrMsg(e: unknown, fallback: string): string {
  const data = (e as { data?: { error?: string } })?.data;
  return (data && typeof data.error === "string" && data.error) || fallback;
}

const ChatStyleCard: React.FC = () => {
  const { data, isLoading } = useGetChatNotificationStyleQuery();
  const [saveStyle, { isLoading: saving }] = useSetChatNotificationStyleMutation();
  const [sendTest, { isLoading: testing }] = useSendChatStyleTestMutation();
  const [selected, setSelected] = useState<ChatNotificationStyle>("sender_title");
  const [testingChannel, setTestingChannel] = useState<"general" | "personal" | null>(null);

  useEffect(() => {
    if (data?.style) setSelected(data.style);
  }, [data?.style]);

  const styles: ChatNotificationStyle[] = data?.styles ?? ["sender_title", "single_line"];
  const dirty = !!data && data.style !== selected;

  const onSave = async () => {
    try {
      await saveStyle(selected).unwrap();
      toast.success("Chat notification style saved");
    } catch (e) {
      toast.error(rtkErrMsg(e, "Failed to save style"));
    }
  };

  const onTest = async (channel: "general" | "personal") => {
    setTestingChannel(channel);
    try {
      await sendTest({ style: selected, channel }).unwrap();
      toast.success(`Test ${channel} notification sent — check your device`);
    } catch (e) {
      toast.error(rtkErrMsg(e, "Failed to send test notification"));
    } finally {
      setTestingChannel(null);
    }
  };

  return (
    <Card className="nadm-card">
      <h2 style={{ marginTop: 0, fontSize: "1.05rem" }}>Chat notification style</h2>
      <Hint>
        How sitebot chat pushes render on your lock screen — for both the shared{" "}
        <strong>general</strong> chat and your <strong>personal</strong> 1:1. Pick a style, preview it
        below, then <strong>Save</strong>. Use <strong>Send test</strong> to fire a sample push to your
        own subscribed devices (enable browser notifications in your profile first).
      </Hint>

      {isLoading ? (
        <p style={{ color: "#888" }}>Loading…</p>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {styles.map((s) => {
              const meta = STYLE_LABELS[s] ?? { label: s, desc: "" };
              const previews = [
                { c: "general" as const, p: chatPreview(s, "general") },
                { c: "personal" as const, p: chatPreview(s, "personal") },
              ];
              return (
                <label
                  key={s}
                  style={{
                    display: "block",
                    border: `2px solid ${selected === s ? "#e8743b" : "#e2e5ea"}`,
                    borderRadius: 12,
                    padding: 12,
                    cursor: "pointer",
                    background: selected === s ? "#fff7f2" : "#fff",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="radio"
                      name="chat-style"
                      checked={selected === s}
                      onChange={() => setSelected(s)}
                    />
                    <span style={{ fontWeight: 600 }}>{meta.label}</span>
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "#555", margin: "4px 0 10px 24px" }}>
                    {meta.desc}
                  </div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginLeft: 24 }}>
                    {previews.map(({ c, p }) => (
                      <div key={c}>
                        <div
                          style={{
                            fontSize: "0.72rem",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            color: "#999",
                            marginBottom: 4,
                          }}
                        >
                          {c}
                        </div>
                        <div style={previewBox}>
                          <img src="/logo_medium.png" alt="" style={previewIcon} />
                          <div style={{ minWidth: 0 }}>
                            <div style={previewTitle}>{p.title}</div>
                            <div style={previewBody}>{p.body}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </label>
              );
            })}
          </div>

          <div
            className="nadm-row"
            style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}
          >
            <BasicButton
              text={saving ? "Saving…" : dirty ? "Save style" : "Saved"}
              onClick={() => void onSave()}
              disabled={saving || !dirty}
            />
            <span style={{ width: 1, height: 20, background: "#ddd" }} />
            <BasicButton
              text={testing && testingChannel === "general" ? "Sending…" : "Send test (general)"}
              onClick={() => void onTest("general")}
              disabled={testing}
            />
            <BasicButton
              text={testing && testingChannel === "personal" ? "Sending…" : "Send test (personal)"}
              onClick={() => void onTest("personal")}
              disabled={testing}
            />
          </div>
          <Hint style={{ marginTop: 8, marginBottom: 0 }}>
            Test pushes use the <em>currently selected</em> style (no need to save first). General chat
            is push-only; personal chat falls back to Telegram if you have no push subscription.
          </Hint>
        </>
      )}
    </Card>
  );
};

// --- voice messages: fun summary toggle + tone prompt + live preview ---

const VoiceFunSummaryCard: React.FC = () => {
  const { data, isLoading } = useGetVoiceConfigQuery();
  const [save, { isLoading: saving }] = useSetVoiceConfigMutation();
  const [runTest, { isLoading: testing }] = useTestVoiceConfigMutation();

  const [enabled, setEnabled] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [sample, setSample] = useState(
    "ok so um, i didn't really like the aotd today, it was kinda boring honestly, like, meh",
  );
  const [preview, setPreview] = useState<{ cleaned: string; summary: string; funSummary: string } | null>(null);

  useEffect(() => {
    if (data?.config) {
      setEnabled(data.config.funSummaryEnabled);
      setPrompt(data.config.funSummaryPrompt);
    }
  }, [data?.config]);

  const dirty =
    !!data && (data.config.funSummaryEnabled !== enabled || data.config.funSummaryPrompt !== prompt);

  const onSave = async () => {
    try {
      await save({ funSummaryEnabled: enabled, funSummaryPrompt: prompt }).unwrap();
      toast.success("Voice fun-summary config saved");
    } catch (e) {
      toast.error(rtkErrMsg(e, "Failed to save voice config"));
    }
  };

  const onTest = async () => {
    if (!sample.trim()) return;
    try {
      const res = await runTest({ transcript: sample, funSummaryPrompt: prompt, funSummaryEnabled: enabled }).unwrap();
      if (res.data) {
        setPreview({ cleaned: res.data.cleaned, summary: res.data.summary, funSummary: res.data.funSummary });
      }
    } catch (e) {
      toast.error(rtkErrMsg(e, "Failed to preview fun summary"));
    }
  };

  return (
    <Card className="nadm-card">
      <h2 style={{ marginTop: 0, fontSize: "1.05rem" }}>Voice messages — fun summary</h2>
      <Hint>
        Voice notes (chat + coffee) are transcribed, cleaned up, and turned into a playful{" "}
        <strong>fun summary</strong> other users see instead of the words. Toggle it on/off globally and
        tune the tone prompt. Use <strong>Preview</strong> to see how a sample transcript turns out
        before saving (uses the unsaved prompt below).
      </Hint>

      {isLoading ? (
        <p style={{ color: "#888" }}>Loading…</p>
      ) : (
        <>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span style={{ fontWeight: 600 }}>Show fun summaries</span>
            <span style={{ fontSize: "0.8rem", color: "#777" }}>
              {enabled ? "on — voice notes get a vibe line" : "off — voice notes show no fun summary"}
            </span>
          </label>

          <label style={{ display: "block", fontSize: "0.82rem", color: "#666", marginBottom: 4 }}>
            Tone prompt
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            className="nadm-input-full"
            style={{
              width: "100%",
              border: "1px solid #ccc",
              borderRadius: 6,
              padding: "8px 10px",
              fontSize: "0.82rem",
              boxSizing: "border-box",
              fontFamily: "inherit",
            }}
          />
          <div className="nadm-row" style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
            <BasicButton
              text={saving ? "Saving…" : dirty ? "Save" : "Saved"}
              onClick={() => void onSave()}
              disabled={saving || !dirty}
            />
            {data?.defaultPrompt && (
              <BasicButton text="Reset prompt to default" onClick={() => setPrompt(data.defaultPrompt)} />
            )}
          </div>

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #eee" }}>
            <label style={{ display: "block", fontSize: "0.82rem", color: "#666", marginBottom: 4 }}>
              Sample transcript
            </label>
            <textarea
              value={sample}
              onChange={(e) => setSample(e.target.value)}
              rows={2}
              className="nadm-input-full"
              style={{
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 6,
                padding: "8px 10px",
                fontSize: "0.82rem",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
            />
            <div style={{ marginTop: 8 }}>
              <BasicButton
                text={testing ? "Generating…" : "Preview"}
                onClick={() => void onTest()}
                disabled={testing || !sample.trim()}
              />
            </div>
            {preview && (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={previewBox}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ ...previewTitle, color: "#999", fontSize: "0.72rem", textTransform: "uppercase" }}>
                      cleaned
                    </div>
                    <div style={previewBody}>{preview.cleaned || "—"}</div>
                  </div>
                </div>
                <div style={previewBox}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ ...previewTitle, color: "#999", fontSize: "0.72rem", textTransform: "uppercase" }}>
                      summary
                    </div>
                    <div style={previewBody}>{preview.summary || "—"}</div>
                  </div>
                </div>
                <div style={{ ...previewBox, background: "#fff7f2", borderColor: "#f0c9b3" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ ...previewTitle, color: "#e8743b", fontSize: "0.72rem", textTransform: "uppercase" }}>
                      fun summary
                    </div>
                    <div style={{ ...previewBody, fontStyle: "italic" }}>
                      {enabled ? preview.funSummary || "—" : "(disabled — nothing shown)"}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

// --- voice activity log ---

const SURFACE_LABEL: Record<VoiceSurface, string> = {
  chat_group: "chat · general",
  chat_personal: "chat · personal",
  coffee: "coffee",
};

function fmtDur(ms?: number): string {
  if (!ms || ms <= 0) return "—";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const VoiceLogRow: React.FC<{ ev: VoiceLogEvent; open: boolean; onToggle: () => void }> = ({
  ev,
  open,
  onToggle,
}) => {
  const token = useAppSelector((s) => s.auth.token);
  return (
  <>
    <tr>
      <td>
        <button
          type="button"
          onClick={onToggle}
          style={{ border: "none", background: "none", cursor: "pointer", padding: 0, color: "#1a4", fontSize: "0.8rem" }}
        >
          {open ? "▾" : "▸"} {fmtTs(ev.createdAt)}
        </button>
      </td>
      <td><code style={{ fontSize: "0.72rem" }}>{SURFACE_LABEL[ev.surface] ?? ev.surface}</code></td>
      <td style={{ fontSize: "0.78rem" }}>{ev.username}</td>
      <td style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>{fmtDur(ev.durationMs)}</td>
      <td style={{ fontSize: "0.82rem", fontStyle: "italic", maxWidth: 280, wordBreak: "break-word" }}>
        {ev.funSummary || (ev.funSummaryEnabled ? "—" : "(disabled)")}
      </td>
    </tr>
    {open && (
      <tr>
        <td colSpan={5} style={{ background: "#fafafa" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "4px 2px" }}>
            {ev.audioUrl && <audio controls src={voiceAudioSrc(ev.audioUrl, token)} style={{ height: 34, maxWidth: 360 }} />}
            <div style={{ fontSize: "0.8rem" }}>
              <strong>Cleaned:</strong> {ev.cleaned || "—"}
            </div>
            <div style={{ fontSize: "0.8rem", color: "#555" }}>
              <strong>Summary:</strong> {ev.summary || "—"}
            </div>
            <details>
              <summary style={{ fontSize: "0.78rem", cursor: "pointer", color: "#777" }}>raw transcript</summary>
              <div style={{ fontSize: "0.78rem", color: "#666", marginTop: 4 }}>{ev.rawTranscript || "—"}</div>
            </details>
            <div style={{ fontSize: "0.72rem", color: "#999" }}>
              transcribe: <code>{ev.transcribeModel || "?"}</code> · cleanup: <code>{ev.cleanupModel || "?"}</code> · lang:{" "}
              <code>{ev.language}</code>
            </div>
          </div>
        </td>
      </tr>
    )}
  </>
  );
};

const VoiceLogsCard: React.FC = () => {
  const [surface, setSurface] = useState<VoiceSurface | "all">("all");
  const { data, isFetching, refetch } = useGetVoiceLogsQuery(
    surface === "all" ? { limit: 100 } : { limit: 100, surface },
  );
  const [expanded, setExpanded] = useState<string | null>(null);
  const events = data?.events ?? [];

  return (
    <Card className="nadm-card">
      <h2 style={{ marginTop: 0, fontSize: "1.05rem" }}>Voice activity</h2>
      <Hint>
        Every processed voice note (chat + coffee): who recorded it, the cleaned transcript, the summary
        + fun summary, the models used, and the audio. Newest first.
      </Hint>
      <div className="nadm-row" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", fontSize: "0.8rem", gap: 2 }}>
          <span style={{ color: "#666" }}>Where</span>
          <select
            value={surface}
            onChange={(e) => setSurface(e.target.value as VoiceSurface | "all")}
            style={{ border: "1px solid #ccc", borderRadius: 4, padding: "4px 6px", background: "#fff" }}
          >
            <option value="all">All</option>
            <option value="chat_group">chat · general</option>
            <option value="chat_personal">chat · personal</option>
            <option value="coffee">coffee</option>
          </select>
        </label>
        <BasicButton text={isFetching ? "Loading…" : "Refresh"} onClick={() => void refetch()} disabled={isFetching} />
      </div>
      {isFetching && events.length === 0 ? (
        <p style={{ color: "#888" }}>Loading…</p>
      ) : events.length === 0 ? (
        <p style={{ fontSize: "0.85rem", color: "#888" }}>No voice notes yet.</p>
      ) : (
        <Scroll className="nadm-table-wrap">
          <Table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Where</th>
                <th>User</th>
                <th>Dur</th>
                <th>Fun summary</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <VoiceLogRow
                  key={ev._id}
                  ev={ev}
                  open={expanded === ev._id}
                  onToggle={() => setExpanded(expanded === ev._id ? null : ev._id)}
                />
              ))}
            </tbody>
          </Table>
        </Scroll>
      )}
    </Card>
  );
};

const NotificationsAdmin: React.FC = () => {
  const user = useAppSelector((state) => state.auth);
  const isAdmin = checkRole(user.role ?? "", "admin");
  const ready = user.bootstrapStatus === "ready";
  const confirm = useConfirm();

  const {
    data: defsData,
    isLoading: defsLoading,
    error: defsError,
    refetch: refetchDefs,
  } = useGetNotifDefsQuery({ limit: 100 }, { skip: !isAdmin || !ready });
  const { data: mutesData, refetch: refetchMutes } = useGetNotifMutesQuery(undefined, {
    skip: !isAdmin || !ready,
  });

  const [updateDef, { isLoading: savingDef }] = useUpdateNotifDefMutation();
  const [muteNotif] = useMuteNotifMutation();
  const [unmuteNotif] = useUnmuteNotifMutation();
  const [setServicePolicy, { isLoading: savingPolicy }] = useSetServicePolicyMutation();
  const [fetchDef] = useLazyGetNotifDefQuery();

  const defs = useMemo(() => defsData?.defs ?? [], [defsData]);
  const mutedCodes = useMemo(
    () => new Set((mutesData ?? []).map((m) => m.code)),
    [mutesData],
  );

  // inline-edit draft for displayName + defaultEmoji, keyed by code (EventsAdmin pattern)
  const [drafts, setDrafts] = useState<Record<string, { displayName: string; defaultEmoji: string }>>(
    {},
  );
  useEffect(() => {
    const next: Record<string, { displayName: string; defaultEmoji: string }> = {};
    for (const d of defs) {
      next[d.code] = { displayName: d.displayName ?? "", defaultEmoji: d.defaultEmoji ?? "" };
    }
    setDrafts(next);
  }, [defs]);

  // filters
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [mutedOnly, setMutedOnly] = useState(false);

  const services = useMemo(
    () => Array.from(new Set(defs.map((d) => d.serviceClientId).filter(Boolean))) as string[],
    [defs],
  );

  const filteredDefs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return defs.filter((d) => {
      if (mutedOnly && !mutedCodes.has(d.code)) return false;
      if (serviceFilter !== "all" && (d.serviceClientId ?? "") !== serviceFilter) return false;
      if (q) {
        const hay = `${d.code} ${d.displayName ?? ""} ${d.description ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [defs, search, serviceFilter, mutedOnly, mutedCodes]);

  // expandable samples
  const [expanded, setExpanded] = useState<string | null>(null);
  const [samplesByCode, setSamplesByCode] = useState<Record<string, NotifSample[]>>({});

  const onToggleExpand = useCallback(
    async (code: string) => {
      if (expanded === code) {
        setExpanded(null);
        return;
      }
      setExpanded(code);
      if (!samplesByCode[code]) {
        try {
          const res = await fetchDef(code).unwrap();
          setSamplesByCode((prev) => ({ ...prev, [code]: res.samples ?? [] }));
        } catch {
          setSamplesByCode((prev) => ({ ...prev, [code]: [] }));
        }
      }
    },
    [expanded, samplesByCode, fetchDef],
  );

  const isDirty = (d: NotifDef): boolean => {
    const draft = drafts[d.code];
    if (!draft) return false;
    return draft.displayName !== (d.displayName ?? "") || draft.defaultEmoji !== (d.defaultEmoji ?? "");
  };

  const handleSaveDef = async (d: NotifDef) => {
    const draft = drafts[d.code];
    if (!draft) return;
    try {
      await updateDef({
        code: d.code,
        patch: {
          displayName: draft.displayName,
          defaultEmoji: draft.defaultEmoji === "" ? null : draft.defaultEmoji,
        },
      }).unwrap();
      toast.success(`Saved ${d.code}`);
    } catch (e: unknown) {
      toast.error(`Save failed: ${errMsg(e)}`);
    }
  };

  const handleToggleMute = async (code: string, currentlyMuted: boolean) => {
    if (currentlyMuted) {
      try {
        await unmuteNotif(code).unwrap();
        toast.success(`Unmuted ${code}`);
      } catch (e: unknown) {
        toast.error(`Unmute failed: ${errMsg(e)}`);
      }
      return;
    }
    const ok = await confirm({
      title: `Mute ${code}?`,
      message:
        "While muted, every user-facing send for this code is skipped at the messenger (logged as muted-by-admin). Broadcasts and actions for this code are blocked too.",
      tone: "danger",
      confirmLabel: "Mute",
    });
    if (!ok) return;
    try {
      await muteNotif({ code }).unwrap();
      toast.success(`Muted ${code}`);
    } catch (e: unknown) {
      toast.error(`Mute failed: ${errMsg(e)}`);
    }
  };

  // --- delivery policy card state (write-only; see hint) ---
  const [policyDrafts, setPolicyDrafts] = useState<
    Record<string, { primary: PrimaryChoice; fallback: FallbackChoice }>
  >({});
  const [customService, setCustomService] = useState("");

  const policyFor = (svc: string) =>
    policyDrafts[svc] ?? { primary: "tg" as PrimaryChoice, fallback: "none" as FallbackChoice };

  const setPolicyField = (
    svc: string,
    field: "primary" | "fallback",
    value: PrimaryChoice | FallbackChoice,
  ) => {
    setPolicyDrafts((prev) => ({
      ...prev,
      [svc]: { ...policyFor(svc), [field]: value },
    }));
  };

  const handleSavePolicy = async (svc: string) => {
    const target = svc.trim();
    if (!target) {
      toast.error("Enter a service client id first");
      return;
    }
    const draft = policyFor(target);
    try {
      await setServicePolicy({
        serviceClientId: target,
        primary: draft.primary === "fanout" ? undefined : draft.primary,
        fallback: draft.primary === "fanout" || draft.fallback === "none" ? null : draft.fallback,
      }).unwrap();
      toast.success(
        draft.primary === "fanout"
          ? `Cleared policy for ${target} (parallel fan-out)`
          : `Saved policy for ${target}`,
      );
    } catch (e: unknown) {
      toast.error(`Policy save failed: ${errMsg(e)}`);
    }
  };

  // --- recent activity (audit log) ---
  const [fetchLogs, { isFetching: logsFetching }] = useLazyGetNotificationLogsQuery();
  const [logs, setLogs] = useState<NotificationLogEntry[]>([]);
  const [codeFilter, setCodeFilter] = useState("");

  const loadLogs = useCallback(async () => {
    try {
      const res = await fetchLogs({ limit: 100 }).unwrap();
      setLogs(res.logs ?? []);
    } catch {
      setLogs([]);
    }
  }, [fetchLogs]);

  useEffect(() => {
    if (isAdmin && ready) void loadLogs();
  }, [isAdmin, ready, loadLogs]);

  const filteredLogs = useMemo(() => {
    const q = codeFilter.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((l) => {
      const code = typeof l.metadata?.code === "string" ? (l.metadata.code as string) : "";
      return code.toLowerCase().includes(q);
    });
  }, [logs, codeFilter]);

  const refreshAll = () => {
    void refetchDefs();
    void refetchMutes();
    void loadLogs();
  };

  if (!isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (!ready) {
    return (
      <PageContainer className="nadm-page">
        <p>Loading session…</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="nadm-page">
      <style>{`
        @media (max-width: 700px) {
          .nadm-page { padding: 12px 8px !important; }
          .nadm-card { padding: 12px !important; }
          .nadm-table-wrap { margin: 0 -8px; }
          .nadm-row { flex-wrap: wrap !important; gap: 8px !important; }
          .nadm-input-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
          .nadm-filter-label { flex: 1 1 100% !important; min-width: 0 !important; }
        }
      `}</style>
      <div className="nadm-row" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>Notifications</h1>
        <Link to="/admin">← Admin home</Link>
        <BasicButton text="Refresh" onClick={refreshAll} />
      </div>
      <Hint>
        Every code the backend has ever sent is auto-registered by the messenger. Edit the display
        name / default emoji per code, mute a code to stop all user-facing sends, and configure the
        per-service delivery policy. Counts come from the messenger registry; the audit log at the
        bottom is the backend&apos;s own send history.
      </Hint>

      <NotificationReachCard />

      {/* chat notification style */}
      <ChatStyleCard />

      {/* voice fun summary */}
      <VoiceFunSummaryCard />

      {/* voice activity log */}
      <VoiceLogsCard />

      {/* delivery policy */}
      <Card className="nadm-card">
        <h2 style={{ marginTop: 0, fontSize: "1.05rem" }}>Service delivery policy</h2>
        <Hint>
          Set a service&apos;s primary channel with an optional fallback (messenger only falls back
          on a <em>known</em> failure — bot blocked, no link, no push sub). Choose{" "}
          <strong>fan-out</strong> to clear back to parallel delivery. Current values are write-only
          here (the proxy exposes the setter, not a reader), so selects start at defaults.
        </Hint>
        <Scroll className="nadm-table-wrap">
          <Table>
            <thead>
              <tr>
                <th>Service client id</th>
                <th>Primary</th>
                <th>Fallback</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {services.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ color: "#888" }}>
                    No services discovered yet — use the custom row below.
                  </td>
                </tr>
              )}
              {services.map((svc) => (
                <PolicyRow
                  key={svc}
                  service={svc}
                  draft={policyFor(svc)}
                  onChange={(f, v) => setPolicyField(svc, f, v)}
                  onSave={() => void handleSavePolicy(svc)}
                  saving={savingPolicy}
                />
              ))}
              {/* custom service row */}
              <tr>
                <td>
                  <input
                    type="text"
                    placeholder="custom service client id"
                    value={customService}
                    onChange={(e) => setCustomService(e.target.value)}
                    className="nadm-input-full"
                    style={{ border: "1px solid #ccc", borderRadius: 4, padding: "4px 6px", minWidth: 220, maxWidth: "100%" }}
                  />
                </td>
                <td>
                  <PrimarySelect
                    value={policyFor(customService).primary}
                    onChange={(v) => setPolicyField(customService, "primary", v)}
                  />
                </td>
                <td>
                  <FallbackSelect
                    value={policyFor(customService).fallback}
                    onChange={(v) => setPolicyField(customService, "fallback", v)}
                  />
                </td>
                <td>
                  <BasicButton
                    text="Save"
                    onClick={() => void handleSavePolicy(customService)}
                    disabled={savingPolicy || !customService.trim()}
                  />
                </td>
              </tr>
            </tbody>
          </Table>
        </Scroll>
      </Card>

      {/* codes table */}
      <Card className="nadm-card">
        <h2 style={{ marginTop: 0, fontSize: "1.05rem" }}>Notification codes</h2>
        <div className="nadm-row" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
          <label className="nadm-filter-label" style={{ display: "flex", flexDirection: "column", fontSize: "0.8rem", gap: 2 }}>
            <span style={{ color: "#666" }}>Search</span>
            <input
              type="text"
              placeholder="code, name, description"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="nadm-input-full"
              style={{ border: "1px solid #ccc", borderRadius: 4, padding: "4px 6px", minWidth: 220, maxWidth: "100%" }}
            />
          </label>
          <label className="nadm-filter-label" style={{ display: "flex", flexDirection: "column", fontSize: "0.8rem", gap: 2 }}>
            <span style={{ color: "#666" }}>Service</span>
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="nadm-input-full"
              style={{ border: "1px solid #ccc", borderRadius: 4, padding: "4px 6px", background: "#fff", maxWidth: "100%" }}
            >
              <option value="all">All services</option>
              {services.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.8rem" }}>
            <input type="checkbox" checked={mutedOnly} onChange={(e) => setMutedOnly(e.target.checked)} />
            <span>Muted only</span>
          </label>
        </div>

        {defsLoading && <p>Loading codes…</p>}
        {!!defsError && <p style={{ color: "#a11" }}>Failed to load notification codes.</p>}

        {!defsLoading && !defsError && (
          <Scroll className="nadm-table-wrap">
            <Table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Display name</th>
                  <th style={emojiCell}>Emoji</th>
                  <th>Service</th>
                  <th>Last sent</th>
                  <th style={numCell}>Sent</th>
                  <th style={numCell}>Skip</th>
                  <th>Muted?</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredDefs.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ color: "#888" }}>
                      No codes match the current filters.
                    </td>
                  </tr>
                )}
                {filteredDefs.map((d) => {
                  const draft = drafts[d.code] ?? { displayName: "", defaultEmoji: "" };
                  const muted = mutedCodes.has(d.code);
                  const dirty = isDirty(d);
                  const isOpen = expanded === d.code;
                  return (
                    <React.Fragment key={d.code}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            onClick={() => void onToggleExpand(d.code)}
                            title="Show recent samples"
                            style={{
                              border: "none",
                              background: "none",
                              cursor: "pointer",
                              fontFamily: "ui-monospace, monospace",
                              fontSize: "0.8rem",
                              padding: 0,
                              color: "#1a4",
                            }}
                          >
                            {isOpen ? "▾" : "▸"} {d.code}
                          </button>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={draft.displayName}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [d.code]: { ...draft, displayName: e.target.value },
                              }))
                            }
                            className="nadm-input-full"
                            style={{ border: "1px solid #ddd", borderRadius: 4, padding: "3px 6px", minWidth: 160, maxWidth: "100%" }}
                          />
                        </td>
                        <td style={emojiCell}>
                          <input
                            type="text"
                            value={draft.defaultEmoji}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [d.code]: { ...draft, defaultEmoji: e.target.value },
                              }))
                            }
                            style={{ border: "1px solid #ddd", borderRadius: 4, padding: "3px 6px", width: 48, textAlign: "center" }}
                          />
                        </td>
                        <td>
                          <code style={{ fontSize: "0.72rem" }}>{d.serviceClientId ?? "—"}</code>
                        </td>
                        <td style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>{fmtTs(d.lastSeenAt)}</td>
                        <td style={numCell}>{d.sendCount ?? 0}</td>
                        <td style={numCell}>{d.skipCount ?? 0}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() => void handleToggleMute(d.code, muted)}
                            style={{
                              ...statusStyle(muted ? "failed" : "sent"),
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            {muted ? "muted" : "live"}
                          </button>
                        </td>
                        <td>
                          {dirty && (
                            <BasicButton
                              text={savingDef ? "…" : "Save"}
                              onClick={() => void handleSaveDef(d)}
                              disabled={savingDef}
                            />
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={9} style={{ background: "#fafafa" }}>
                            <SamplesBlock samples={samplesByCode[d.code]} description={d.description} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </Scroll>
        )}
      </Card>

      {/* recent activity */}
      <Card className="nadm-card">
        <h2 style={{ marginTop: 0, fontSize: "1.05rem" }}>Recent activity</h2>
        <Hint>Last 100 outbound sends from the backend audit log. Filter by code (reads metadata.code).</Hint>
        <div className="nadm-row" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
          <label className="nadm-filter-label" style={{ display: "flex", flexDirection: "column", fontSize: "0.8rem", gap: 2, flex: 1, minWidth: 200 }}>
            <span style={{ color: "#666" }}>Code contains</span>
            <input
              type="text"
              placeholder="e.g. aotd.heist, card.pool"
              value={codeFilter}
              onChange={(e) => setCodeFilter(e.target.value)}
              className="nadm-input-full"
              style={{ border: "1px solid #ccc", borderRadius: 4, padding: "4px 6px", maxWidth: "100%" }}
            />
          </label>
          <BasicButton text="Reload" onClick={() => void loadLogs()} disabled={logsFetching} />
        </div>
        {logsFetching && logs.length === 0 && <p>Loading activity…</p>}
        <LogScroll>
          <Scroll className="nadm-table-wrap" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table>
            <thead>
              <tr>
                <th>Time (UTC)</th>
                <th>Code</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Recipient</th>
                <th>Message</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((row) => {
                const code = typeof row.metadata?.code === "string" ? (row.metadata.code as string) : "—";
                return (
                  <tr key={row._id}>
                    <td style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>{fmtTs(row.timestamp)}</td>
                    <td>
                      <code style={{ fontSize: "0.72rem" }}>{code}</code>
                    </td>
                    <td>
                      <code style={{ fontSize: "0.72rem" }}>{row.channel}</code>
                    </td>
                    <td>
                      <span style={statusStyle(row.status)}>{row.status}</span>
                    </td>
                    <td>
                      <code style={{ fontSize: "0.72rem" }}>{row.recipient}</code>
                    </td>
                    <td style={{ fontSize: "0.78rem", maxWidth: 320, wordBreak: "break-word" }}>{row.message}</td>
                    <td style={{ fontSize: "0.72rem", maxWidth: 200, wordBreak: "break-word" }}>
                      {row.statusDetail ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          </Scroll>
        </LogScroll>
        {filteredLogs.length === 0 && !logsFetching && (
          <p style={{ fontSize: "0.85rem", color: "#888" }}>No activity matches the current filter.</p>
        )}
      </Card>
    </PageContainer>
  );
};

// small helper to pull a readable error message out of an RTK error
function errMsg(e: unknown): string {
  if (e && typeof e === "object" && "data" in e) {
    const data = (e as { data: unknown }).data;
    if (data && typeof data === "object" && "error" in data) {
      return String((data as { error: unknown }).error);
    }
    return JSON.stringify(data);
  }
  return String(e);
}

const PrimarySelect: React.FC<{ value: PrimaryChoice; onChange: (v: PrimaryChoice) => void }> = ({
  value,
  onChange,
}) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value as PrimaryChoice)}
    className="nadm-input-full"
    style={{ border: "1px solid #ccc", borderRadius: 4, padding: "4px 6px", background: "#fff", maxWidth: "100%" }}
  >
    <option value="tg">tg</option>
    <option value="push">push</option>
    <option value="fanout">fan-out (clear)</option>
  </select>
);

const FallbackSelect: React.FC<{ value: FallbackChoice; onChange: (v: FallbackChoice) => void }> = ({
  value,
  onChange,
}) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value as FallbackChoice)}
    className="nadm-input-full"
    style={{ border: "1px solid #ccc", borderRadius: 4, padding: "4px 6px", background: "#fff", maxWidth: "100%" }}
  >
    <option value="none">none</option>
    <option value="tg">tg</option>
    <option value="push">push</option>
  </select>
);

const PolicyRow: React.FC<{
  service: string;
  draft: { primary: PrimaryChoice; fallback: FallbackChoice };
  onChange: (field: "primary" | "fallback", value: PrimaryChoice | FallbackChoice) => void;
  onSave: () => void;
  saving: boolean;
}> = ({ service, draft, onChange, onSave, saving }) => (
  <tr>
    <td>
      <code style={{ fontSize: "0.75rem" }}>{service}</code>
    </td>
    <td>
      <PrimarySelect value={draft.primary} onChange={(v) => onChange("primary", v)} />
    </td>
    <td>
      <FallbackSelect value={draft.fallback} onChange={(v) => onChange("fallback", v)} />
    </td>
    <td>
      <BasicButton text="Save" onClick={onSave} disabled={saving} />
    </td>
  </tr>
);

const SamplesBlock: React.FC<{ samples?: NotifSample[]; description?: string | null }> = ({
  samples,
  description,
}) => {
  if (samples === undefined) {
    return <span style={{ fontSize: "0.8rem", color: "#888" }}>Loading samples…</span>;
  }
  return (
    <div>
      {description && (
        <p style={{ fontSize: "0.8rem", color: "#555", margin: "4px 0 8px" }}>{description}</p>
      )}
      {samples.length === 0 ? (
        <span style={{ fontSize: "0.8rem", color: "#888" }}>
          No recent samples (messenger history wiring is incremental — backend audit log below covers
          recent traffic).
        </span>
      ) : (
        <Scroll className="nadm-table-wrap">
          <Table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Recipient</th>
                <th>Status</th>
                <th>Message</th>
                <th>Detail</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {samples.slice(0, 20).map((s, i) => (
                <tr key={i}>
                  <td style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>{fmtTs(s.timestamp)}</td>
                  <td style={{ fontSize: "0.72rem" }}>{s.recipient ?? "—"}</td>
                  <td>
                    <span style={statusStyle(s.status ?? "")}>{s.status ?? "—"}</span>
                  </td>
                  <td style={{ fontSize: "0.78rem", maxWidth: 320, wordBreak: "break-word" }}>
                    {s.message ?? "—"}
                  </td>
                  <td style={{ fontSize: "0.72rem", maxWidth: 200, wordBreak: "break-word" }}>
                    {s.statusDetail ?? "—"}
                  </td>
                  <td style={{ fontSize: "0.72rem" }}>{s.source ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      )}
    </div>
  );
};

export default NotificationsAdmin;
